import { Avatar as DSAvatar } from '@ghq-abi/design-system-v2';
import * as React from 'react';
import { useGetProfilePicture } from '~/entities/Employee/api/useGetProfilePicture';
import { sizeToPx } from '~/shared/utils/avatar.utils';
import { invertColor, stringToColor } from '~/shared/utils/color.utils';

type DSAvatarRootProps = React.ComponentProps<typeof DSAvatar.Root>;

type AvatarProps = DSAvatarRootProps & {
  name: string;
  employeeId?: string;
  globalId?: string;
  inList?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

const UI_AVATAR_BASE_URL = 'https://ui-avatars.com/api/';

export function Avatar({ name, employeeId, globalId, inList, ...rest }: AvatarProps) {
  const { data } = useGetProfilePicture(globalId ?? '', !inList);

  const imageSrc = React.useMemo(
    () => (data ? URL.createObjectURL(data) : undefined),
    [data],
  );

  React.useEffect(() => () => {
    if (imageSrc) URL.revokeObjectURL(imageSrc);
  }, [imageSrc]);

  const uiAvatarUrl = React.useMemo(() => {
    const background = stringToColor(name);
    const color = invertColor(background, true);
    const sizePx = sizeToPx(rest.size || 'lg');

    return `${UI_AVATAR_BASE_URL}?name=${encodeURIComponent(
      name,
    )}&background=${background}&color=${color}&rounded=true&size=${sizePx}`;
  }, [name, rest.size]);

  return (
    <DSAvatar.Root {...rest}>
      <DSAvatar.Image src={(employeeId || globalId) && imageSrc ? imageSrc : uiAvatarUrl} alt={name} />
    </DSAvatar.Root>
  );
}
